/**
 * Pure Services domain catalog.
 *
 * This module intentionally lives outside `supabase/functions`: Metro blocks
 * function source from the native bundle, while the Edge Functions need the
 * exact same values. It has no React, Deno, Node or database dependencies.
 */
export const SERVICE_CATEGORY_IDS = ['tow', 'heavy_equipment', 'construction', 'materials'] as const

export type ServiceCategoryId = (typeof SERVICE_CATEGORY_IDS)[number]

export const SERVICE_TIMINGS = ['now', 'scheduled'] as const
export type ServiceTiming = (typeof SERVICE_TIMINGS)[number]

export const SERVICE_WINDOWS = ['morning', 'afternoon', 'flexible'] as const
export type ServiceWindow = (typeof SERVICE_WINDOWS)[number]

export const SERVICE_REQUEST_STATUSES = [
  'submitted',
  'matching',
  'quoted',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
] as const
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number]

/**
 * The only status changes an operator can make. The database repeats this as
 * the authoritative invariant; keeping this pure contract beside the catalog
 * lets customer UI, Edge parsing, Preview, and the operations surface name the
 * same lifecycle without inventing a second set of product states.
 */
export const SERVICE_OPERATOR_TRANSITIONS = {
  submitted: ['matching', 'cancelled'],
  matching: ['quoted', 'cancelled'],
  // A quote can be replaced before the customer accepts it. The repeated
  // `quoted` event makes the revised proposal explicit in the timeline.
  quoted: ['quoted', 'cancelled'],
  confirmed: ['in_progress', 'cancelled'],
  in_progress: ['completed'],
  completed: [],
  cancelled: [],
} as const satisfies Record<ServiceRequestStatus, readonly ServiceRequestStatus[]>

/**
 * A requester can withdraw before committing to an accepted proposal. The
 * database enforces this same boundary; the shared helper prevents mobile,
 * Preview and the Edge response from each inventing their own cancellation
 * rule.
 */
export const SERVICE_REQUESTER_CANCELLABLE_STATUSES = [
  'submitted',
  'matching',
  'quoted',
] as const satisfies readonly ServiceRequestStatus[]

export interface ServiceNeed {
  id: string
  titleKey: string
  /**
   * A tow needs both ends of the movement before operations can safely match
   * it. Other services happen at one worksite, so forcing a second address
   * there would add friction without improving the dispatch brief.
   */
  requiresDestination: boolean
}

export interface ServiceCatalogEntry {
  id: ServiceCategoryId
  titleKey: string
  descriptionKey: string
  actionKey: string
  badgeKey: string
  icon: 'tow' | 'equipment' | 'construction' | 'materials'
  defaultTiming: ServiceTiming
  needs: readonly ServiceNeed[]
}

export const SERVICE_CATALOG: readonly ServiceCatalogEntry[] = [
  {
    id: 'tow',
    titleKey: 'services.categories.tow.title',
    descriptionKey: 'services.categories.tow.description',
    actionKey: 'services.categories.tow.action',
    badgeKey: 'services.categories.tow.badge',
    icon: 'tow',
    defaultTiming: 'now',
    needs: [
      {
        id: 'tow_vehicle',
        titleKey: 'services.needs.tow_vehicle',
        requiresDestination: true,
      },
      {
        id: 'roadside_assistance',
        titleKey: 'services.needs.roadside_assistance',
        requiresDestination: false,
      },
      {
        id: 'flatbed_tow',
        titleKey: 'services.needs.flatbed_tow',
        requiresDestination: true,
      },
    ],
  },
  {
    id: 'heavy_equipment',
    titleKey: 'services.categories.heavy_equipment.title',
    descriptionKey: 'services.categories.heavy_equipment.description',
    actionKey: 'services.categories.heavy_equipment.action',
    badgeKey: 'services.categories.heavy_equipment.badge',
    icon: 'equipment',
    defaultTiming: 'scheduled',
    needs: [
      { id: 'excavation', titleKey: 'services.needs.excavation', requiresDestination: false },
      { id: 'earthmoving', titleKey: 'services.needs.earthmoving', requiresDestination: false },
      { id: 'lifting', titleKey: 'services.needs.lifting', requiresDestination: false },
      { id: 'compaction', titleKey: 'services.needs.compaction', requiresDestination: false },
    ],
  },
  {
    id: 'construction',
    titleKey: 'services.categories.construction.title',
    descriptionKey: 'services.categories.construction.description',
    actionKey: 'services.categories.construction.action',
    badgeKey: 'services.categories.construction.badge',
    icon: 'construction',
    defaultTiming: 'scheduled',
    needs: [
      { id: 'concrete', titleKey: 'services.needs.concrete', requiresDestination: false },
      { id: 'demolition', titleKey: 'services.needs.demolition', requiresDestination: false },
      { id: 'site_labor', titleKey: 'services.needs.site_labor', requiresDestination: false },
      {
        id: 'site_preparation',
        titleKey: 'services.needs.site_preparation',
        requiresDestination: false,
      },
    ],
  },
  {
    id: 'materials',
    titleKey: 'services.categories.materials.title',
    descriptionKey: 'services.categories.materials.description',
    actionKey: 'services.categories.materials.action',
    badgeKey: 'services.categories.materials.badge',
    icon: 'materials',
    defaultTiming: 'scheduled',
    needs: [
      {
        id: 'material_delivery',
        titleKey: 'services.needs.material_delivery',
        requiresDestination: false,
      },
      { id: 'dump_truck', titleKey: 'services.needs.dump_truck', requiresDestination: false },
      {
        id: 'load_transport',
        titleKey: 'services.needs.load_transport',
        requiresDestination: false,
      },
      { id: 'other', titleKey: 'services.needs.other', requiresDestination: false },
    ],
  },
] as const

export interface ServiceLocation {
  label: string
  latitude: number
  longitude: number
}

export interface ServiceRequestInput {
  idempotencyKey: string
  category: ServiceCategoryId
  need: string
  location: ServiceLocation
  /** Required only for needs that physically move something. */
  destination: ServiceLocation | null
  timing: ServiceTiming
  scheduledDate: string | null
  arrivalWindow: ServiceWindow
  details: string
}

export interface ServiceRequestEvent {
  id: string
  kind: ServiceRequestStatus | 'created'
  createdAt: string
}

export interface ServiceQuote {
  amountCents: number
  currency: 'USD'
  summary: string
  createdAt: string
  acceptedAt: string | null
}

export interface ServiceRequest {
  id: string
  category: ServiceCategoryId
  need: string
  location: ServiceLocation
  destination: ServiceLocation | null
  timing: ServiceTiming
  scheduledDate: string | null
  arrivalWindow: ServiceWindow
  details: string
  status: ServiceRequestStatus
  createdAt: string
  updatedAt: string
  canCancel: boolean
  canAcceptQuote: boolean
  quote: ServiceQuote | null
  events: ServiceRequestEvent[]
}

export function isServiceCategory(value: unknown): value is ServiceCategoryId {
  return typeof value === 'string' && SERVICE_CATEGORY_IDS.includes(value as ServiceCategoryId)
}

export function isServiceTiming(value: unknown): value is ServiceTiming {
  return typeof value === 'string' && SERVICE_TIMINGS.includes(value as ServiceTiming)
}

export function isServiceWindow(value: unknown): value is ServiceWindow {
  return typeof value === 'string' && SERVICE_WINDOWS.includes(value as ServiceWindow)
}

export function isServiceRequestStatus(value: unknown): value is ServiceRequestStatus {
  return (
    typeof value === 'string' && SERVICE_REQUEST_STATUSES.includes(value as ServiceRequestStatus)
  )
}

export function canOperatorTransitionServiceRequest(
  from: ServiceRequestStatus,
  to: ServiceRequestStatus,
): boolean {
  return (SERVICE_OPERATOR_TRANSITIONS[from] as readonly ServiceRequestStatus[]).includes(to)
}

export function canRequesterCancelServiceRequest(status: ServiceRequestStatus): boolean {
  return (SERVICE_REQUESTER_CANCELLABLE_STATUSES as readonly ServiceRequestStatus[]).includes(
    status,
  )
}

export function canRequesterAcceptServiceQuote(
  status: ServiceRequestStatus,
  quote: Pick<ServiceQuote, 'acceptedAt'> | null | undefined,
): boolean {
  return status === 'quoted' && quote !== null && quote !== undefined && quote.acceptedAt === null
}

export function serviceCategoryById(id: ServiceCategoryId): ServiceCatalogEntry {
  const category = SERVICE_CATALOG.find((candidate) => candidate.id === id)
  if (!category) throw new Error(`Unknown service category: ${id}`)
  return category
}

export function serviceNeedBelongsToCategory(category: ServiceCategoryId, need: string): boolean {
  return serviceCategoryById(category).needs.some((candidate) => candidate.id === need)
}

/**
 * Gives every caller one answer to the operational question "do we need an
 * end point?". Invalid category/need pairs deliberately return false here;
 * they are rejected by `serviceNeedBelongsToCategory` at each trust boundary.
 */
export function serviceNeedRequiresDestination(category: ServiceCategoryId, need: string): boolean {
  return (
    serviceCategoryById(category).needs.find((candidate) => candidate.id === need)
      ?.requiresDestination === true
  )
}
